<template>
    <div>
        <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>
        <b-overlay :show="!formReady">
            <div v-if="!formReady" class="min-height-200">Loading...</div>
            <FormulateForm
                v-if="formReady"
                :values="initialFormData"
                :name="formName"
                :form-errors="formErrors"
                #default="{ isValid, isLoading, value }"
                @submit="handleSubmit"
            >   <!-- we had :keep-model-data="true" on form level, but it was unreliable, kept stuff but didn't show it again when toggled back on! -->
                <FormulateInput
                    name="comp_item_id"
                    key="comp_item_id"
                    type="select"
                    label="Document Type"
                    help="Available options are based on your permissions and existing documents for this vendor."
                    validation="required"
                    validation-name="Document Type"
                    :option-groups="itemOptionGroups"
                    :disabled="isLocked"
                    :outer-class="['formulate-horizontal']"
                    @input="handleCompItemSelection"
                />  <!-- :options="itemOptions" -->
                <!-- TODO - hide title for NON-SCOPEABLE CompItems -->
                <!-- v-if="value && value.comp_item_id && compItemOptionsById[value.comp_item_id] && compItemOptionsById[value.comp_item_id].allow_vendor_scopes" -->
                <!-- selectedCompItem && selectedCompItem.id && selectedCompItem.allow_vendor_scopes -->
                <FormulateInput
                    v-if="showTitleField"
                    name="title"
                    key="title"
                    type="text"
                    focus
                    label="Title"
                    placeholder="Title of this item"
                    help="A descriptive name, visible to suppliers and internal users"
                    validation="required"
                    validation-name="Title"
                    :disabled="isLocked"
                    :outer-class="['formulate-horizontal']"
                />
                
                <FormulateInput
                    name="vci_internal_notes"
                    key="vci_internal_notes"
                    type="textarea"
                    label="Internal Notes"
                    placeholder="Add any general notes about this document for this vendor. Only visible to internal users."
                    rows="3"
                    help="Optional"
                    validation-name="Internal Notes"
                    :disabled="isLocked"
                    :outer-class="['formulate-horizontal']"
                />
                <hr>
                <!-- :validation="value && value.comp_item_id && compItemOptionsById[value.comp_item_id] && compItemOptionsById[value.comp_item_id].comp_subs_file_required ? 'required' : ''" -->
                <FormulateInput
                    id="compsubfile-formulate-file-input"
                    ref="formulateFileInput"
                    type="file"
                    name="files"
                    key="files"
                    label="Files"
                    :validation="selectedCompItem && selectedCompItem.id && selectedCompItem.comp_subs_file_required ? 'required' : ''"
                    validation-name="File(s)"
                    :disabled="isLocked"
                    :file-remove-class="isLocked ? 'd-none' : []"
                    :upload-area-mask-class="['bg-gray-100']"
                    :outer-class="['formulate-horizontal']"
                    :uploader="formulateUploader"
                    upload-behavior="live"
                    multiple     
                />
                <FormulateInput
                    name="notes"
                    key="notes"
                    type="textarea"
                    label="Notes about this version"
                    placeholder="Add any notes specific to this first version.  Visible to anyone who can see this document."
                    rows="2"
                    help="Optional"
                    validation-name="Version Notes"
                    :disabled="isLocked"
                    :outer-class="['formulate-horizontal']"
                />
                <hr>
                <!-- <FormulateInput
                    name="enableExpiration"
                    key="enableExpiration"
                    type="checkbox"
                    label="Track Expiration / Valid Dates?"
                    class="mb-1"
                /> -->
                <FormulateInput
                    v-if="selectedCompItem && showEnableExpirationField"
                    name="enableExpirationChoice"
                    key="enableExpirationChoice"
                    type="radio"
                    label="Track Expiration / Valid Dates?"
                    :options="{yes: 'Yes', no: 'No'}"
                    class="mb-1"
                    :wrapper-class="['d-flex', 'gap-4']"
                    :element-class="['d-flex', 'gap-3']"
                />
                <div v-if="selectedCompItem && (mustShowExpirationFields || (value && value.enableExpirationChoice == 'yes'))" class="border rounded-lg p-3 bg-white">  <!--   -->
                    <div class="row">
                        <div class="col-6">
                            <FormulateInput
                                v-if="!mustShowValidFrom"
                                name="validFromMode"
                                key="validFromMode"
                                type="radio"
                                label="Valid From"
                                :options="[
                                    { value: 'forever', label: 'No Limit' },
                                    { value: 'date', label: 'Specific Date' },
                                ]"
                                validation="required"
                                validation-name="Valid From selection"
                                :keep-model-data="true"
                                :disabled="isLocked || validFromDisabled"
                                :label-class="['font-size-100', 'mb-3', 'pb-2', 'border-bottom']"
                            ></FormulateInput>
                            <FormulateInput
                                v-if="value && (value.validFromMode === 'date' || mustShowValidFrom)"
                                name="valid_from"
                                key="valid_from"
                                type="bootstrap_date"
                                inline
                                label="Valid From Date"
                                :validation="`required`"
                                validation-name="Valid From date"
                                :keep-model-data="true"
                                :disabled="isLocked || validFromDisabled"
                                style="min-height: 425px;"
                            ></FormulateInput>  <!-- v-if="value && value.validFromMode === 'date'" -->
                            <!-- setting min-height here to prevent UI from jumping around as people scroll through months with different numbers of weeks -->
                        </div>
                        <!-- TODO - put some validation on from/to dates to check from is before to -->
                        <div class="col-6">
                            <FormulateInput
                                v-if="!mustShowValidTo"
                                name="validToMode"
                                key="validToMode"
                                type="radio"
                                label="Valid To"
                                :options="[
                                    { value: 'forever', label: 'No Limit' },
                                    { value: 'date', label: 'Specific Date' },
                                ]"
                                validation="required"
                                validation-name="Valid To selection"
                                :keep-model-data="true"
                                :disabled="isLocked || validToDisabled"
                                :label-class="['font-size-100', 'mb-3', 'pb-2', 'border-bottom']"
                            ></FormulateInput>
                            <FormulateInput
                                v-if="value && (value.validToMode === 'date' || mustShowValidTo)"
                                name="valid_to"
                                key="valid_to"
                                type="bootstrap_date"
                                inline
                                label="Valid To Date"
                                validation="required"
                                validation-name="Valid To date"
                                :keep-model-data="true"
                                :disabled="isLocked || validToDisabled"
                                style="min-height: 425px;"
                            ></FormulateInput>  <!-- v-if="value && value.validToMode === 'date'" -->
                            <!-- setting min-height here to prevent UI from jumping around as people scroll through months with different numbers of weeks -->
                        </div>
                    </div>
                </div>
                <hr>
                <!-- <FormulateInput
                    v-if="value && value.enableExpirationChoice == 'yes' && value.validToMode === 'date'"
                    name="enableExpireActions"
                    key="enableExpireActions"
                    type="checkbox"
                    label="Set Reminder on Expiration?"
                    class="mb-1"
                /> -->
                <FormulateInput
                    v-if="enableVciEas && value && (mustShowValidTo || (value.enableExpirationChoice == 'yes' && value.validToMode === 'date'))"
                    name="enableExpireActionsChoice"
                    key="enableExpireActionsChoice"
                    type="radio"
                    label="Set Reminder on Expiration?"
                    :options="{yes: 'Yes', no: 'No'}"
                    class="mb-1"
                    :keep-model-data="true"
                    :wrapper-class="['d-flex', 'gap-4']"
                    :element-class="['d-flex', 'gap-3']"
                />
                <!-- value && value.enableExpireActionsChoice == 'yes' && value.enableExpirationChoice == 'yes' -->
                <div v-if="enableVciEas && value && value.enableExpireActionsChoice == 'yes' && (mustShowValidTo || value.enableExpirationChoice == 'yes')" class="border rounded-lg p-3 bg-white">
                    <div class="d-flex gap-4 align-items-center justify-content-center">
                        <div class="">
                            <div class="d-flex">
                                <FormulateInput
                                    name="duration_num_periods"
                                    key="duration_num_periods"
                                    type="number"
                                    label=""
                                    placeholder="1"
                                    validation="required|number|between:0,51"
                                    validation-name="Periods"
                                    min="1"
                                    max="50"
                                    step="1"
                                    :keep-model-data="true"
                                    :disabled="isLocked || value.validToMode === 'forever'"
                                    class="mb-0"
                                ></FormulateInput>
                                <FormulateInput
                                    name="duration_period"
                                    key="duration_period"
                                    type="select"
                                    :options="expireActionPeriodOptions"
                                    label=""
                                    validation-name="Period"
                                    :keep-model-data="true"
                                    :disabled="isLocked || value.validToMode === 'forever'"
                                    class="mb-0"
                                ></FormulateInput>
                            </div>
                        </div>
                        <div class="">
                            <div class="text-center">before expiration</div>
                        </div>
                        <div class="">
                            <FormulateInput
                                name="reminder_action"
                                key="reminder_action"
                                type="select"
                                label=""
                                :options="expireActionOptions"
                                :keep-model-data="true"
                                :disabled="isLocked || value.validToMode === 'forever'"
                            ></FormulateInput>
                        </div>
                    </div>
                </div>

                <!-- <div class="border rounded p-3 my-3">
                    <pre>{{ selectedCompItem }}</pre>
                </div> -->

                <FormulateErrors />

                <div class="mt-4 mb-3 d-flex justify-content-center">
                    <b-button
                        variant="gray-secondary"
                        size="lg"
                        class="mr-3"
                        @click="triggerCancel"
                    >
                        <span>Cancel</span>
                    </b-button>
                    <b-button
                        variant="primary"
                        size="lg"
                        :disabled="!isValid || isLoading || saving || uploadPending || isLocked"
                        @click="triggerSubmit"
                    >
                        <b-spinner small v-if="saving" key="uploading" class="mr-2"></b-spinner>
                        <b-icon v-else key="uploadReady" icon="upload" class="mr-2"></b-icon>
                        <span>{{ saving ? 'Saving' : 'Save' }}</span>
                    </b-button>
                </div>
            </FormulateForm>
        </b-overlay>
    </div>
</template>

<script>
import { errorHandling } from '../../js/custom-vue-mixins';
import { suppliosApi } from '../../js/api-service';
import { asyncSplFileSlinger } from '../../js/file-helpers';
import cloneDeep from 'lodash/cloneDeep';
// import groupBy from 'lodash/groupBy';
import sortBy from 'lodash/sortBy';
import { DateTime } from 'luxon';

const emptyReminderData = {
    duration_num_periods: 1,  // set it to something in the valid range as a default (or blank?)
    duration_period: 'weeks',
    reminder_action: '',
};

const defaultData = {
    title: '',
    instructions: '',
    notes: '',
    vci_internal_notes: '',
    comp_item_id: '',
    // EXPIRATION -- pop stuff off before sending to API 
    //enableExpiration: true,  // default to have this ON
    enableExpirationChoice: 'yes',  // default to have this ON  -- replaces enableExpiration
    validFromMode: 'forever',  // forever or date
    validToMode: 'forever',  // forever or date
    valid_from: null,
    valid_to: null,
    // valid_from_forever: true,
    // valid_to_forever: true,
    // REMINDERS
    //enableExpireActions: false,
    enableExpireActionsChoice: 'no',  // replaces enableExpireActions
    ...emptyReminderData,
    // duration_num_periods: 0,
    // duration_period: 'days',
    // reminder_action: '',
};

const expireActionPeriodOptions = [  // TEMPORARY - should be fetched from API
    { value: 'days', label: 'Days' },
    { value: 'weeks', label: 'Weeks' },
    { value: 'months', label: 'Months' },
];

const expireActionOptions = [  // TEMPORARY - should be fetched from API
    //{ value: 'email_supplier', label: 'Email Supplier' },
    { value: 'email_me', label: 'Assign Follow-Up Task To Me' },  // CREATE A NODE, with a saved message, just deliver that
    //{ value: 'start_renew', label: 'Start Renewal' },
];

export default {
    name: 'ScopedVendorCompItemForm',
    components: {},
    mixins: [
        errorHandling,
    ],
    props: {
        vendorId: {  // always required -- or could we show a picker here?
            type: String,
            required: true,
        },
        existingData: {  // IF DOING UPDATE - handle this later!  TODO - needs work if using for updates!  and should not be allowed to update versions...
            type: Object,
            required: false,
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loading: false,  
            saving: false,
            loadingAvailableItems: false,
            formName: 'scopedVendorCompItemForm',
            formReady: false,  // set true when you're done prepping initial data, if any
            formErrors: [],  // for form-level errors
            uploadPending: false,  // are uploads happening?
            initialFormData: {},  // if you are updating existing, must put here (goes to value prop, not v-model)
            //formData: {},  // just set initialFormData - then get the 'current' data from the @submit handler
            fileCategory: 'comp_sub',  // the s3file category in our backend -- we still make a submission from this, so CompSub is correct place for the file
            // defaults
            compItemOptionsById: {},  // for looking up compItem data by ID
            itemOptions: [],  // fetched from API - flat list -- but we're now using the grouped list instead - itemOptionGroups
            itemOptionGroups: {},  // itemOptions, but grouped by CompItemType label
            expireActionOptions: [...expireActionOptions],
            expireActionPeriodOptions: [...expireActionPeriodOptions],
            // showTitleField: false,  // show/hide title field based on CompItem scopeable status
            // set the selected CompItem object when one is selected (not in a computed, doesn't work well there in the form)
            selectedCompItem: null,  // the CompItem object for the selected comp_item_id (CompItemSubFormSettingsRead)
            enableVciEas: false,  // use this to toggle showing the enableExpireActions field... we should eventually hide here, push people to do it on CompItem
        }
    },
    computed: {
        isLocked() {
            // do we want to lock form?  add logic later...
            return false;
        },
        isUpdate() {
            return !!this.existingData?.id;
        },
        showTitleField() {
            return this.selectedCompItem?.allow_vendor_scopes || false;
        },
        filesRequired() {
            return this.selectedCompItem?.comp_subs_file_required || false;
        },
        // showValidFromField() {  // just disable the field instead, unless both are hidden?
        //     return this.selectedCompItem?.valid_from_manual_input !== 'hidden';
        // },
        // showValidToField() {  // just disable the field instead, unless both are hidden?
        //     return this.selectedCompItem?.valid_to_manual_input !== 'hidden';
        // },
        showEnableExpirationField() {
            // should we show the field that lets them turn on/off expiration tracking?
            // if EITHER valid_from or valid_to is REQUIRED, user cannot turn off expiration tracking, so we hide it
            // if BOTH are hidden, we also hide it... no use when neither is settable
            if (!this.selectedCompItem) {
                return false;  // hide it if we don't have a selected item?  make sure everything reacts properly...
            }
            const compItem = this.selectedCompItem;
            return !(this.mustShowExpirationFields || (compItem.valid_from_manual_input === 'hidden' && compItem.valid_to_manual_input === 'hidden'));
        },
        mustShowValidFrom() {
            // do we HAVE to show the valid_from field?  if it's required on the CompItem, yes
            if (!this.selectedCompItem) {
                return false;
            }
            return this.selectedCompItem.valid_from_manual_input === 'required';
        },
        mustShowValidTo() {
            // do we HAVE to show the valid_to field?  if it's required on the CompItem, yes
            if (!this.selectedCompItem) {
                return false;
            }
            return this.selectedCompItem.valid_to_manual_input === 'required';
        },
        mustShowExpirationFields() {
            return this.mustShowValidFrom || this.mustShowValidTo;
        },
        validFromDisabled() {
            if (!this.selectedCompItem) {
                return false;
            }
            return this.selectedCompItem.valid_from_manual_input === 'hidden';
        },
        validToDisabled() {
            if (!this.selectedCompItem) {
                return false;
            }
            return this.selectedCompItem.valid_to_manual_input === 'hidden';
        },
        validFromRequired() {
            if (!this.selectedCompItem) {
                return false;
            }
            return this.selectedCompItem.valid_from_manual_input === 'required';
        },
        validToRequired() {
            if (!this.selectedCompItem) {
                return false;
            }
            return this.selectedCompItem.valid_to_manual_input === 'required';
        },
    },
    mounted() {
        // do something depending on being a create/update?
        this.getAvailableCompItems().then(() => {
            if (this.isUpdate) {
                this.setInitialFormData(this.existingData);
                this.formReady = true;
            } else {
                this.setInitialFormData(defaultData);
                this.formReady = true;
            }
        });
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        setInitialFormData(subData) {
            // TODO -- if you actualy start using for updates, make sure any existing DATES are in tenant tz correctly
            // pass in existingData from your API, this has to format the 'files' array as Formulate expects
            // multiple file inputs should be an array of objects with at least:
            //   'id' (for us), 'url' key (for formulate) and 'name' is optional (should be filename)
            // our INCOMING data (from output serializer has 'files_list' - nested array of files, and 'files' - just the IDs, used for updates)
            // we need to get the object data from 'files_list' and set it on 'files' here for use in the form
            // THEN, before sending to API, make a flat list of JUST THE IDS of new/updated IDs
            let { files_list, ...otherData } = subData;
            this.initialFormData = cloneDeep(otherData);  // copy this first, then we'll add files back in after 
            if (files_list && Array.isArray(files_list) && files_list.length) {
                let initialFiles = [];
                files_list.forEach(f => {
                    if (!f.id || !f.download_url) {  // could also just use 'public_url' - but this person should have perms to get download_url...
                        return;  // GOTTA HAVE 'id' (for our own backend) and 'url' for formulate
                    }
                    const fileObj = {id: f.id, url: f.download_url};
                    if (f.filename) {
                        fileObj['name'] = f.filename;
                    }
                    initialFiles.push(fileObj);
                })
                this.initialFormData['files'] = initialFiles;  // as long as 'files' is still the key you want it as when posting to API
            }
        },
        triggerSubmit() {
            this.$formulate.submit(this.formName);
        },
        triggerCancel() {
            this.$emit('cancel');  // also need to reset things?  or let it get destroyed?
        },
        async getAvailableCompItems() {
            // NEW Nov 26 2024 - replaces getItemTypes after our data model change
            this.loadingAvailableItems = true;
            let resp;
            try {
                resp = await suppliosApi.compItemStd.get_comp_items_can_add_for_vendor(this.vendorId);
                const rawItems = Array.isArray(resp.data) ? resp.data : [];  // need to remap to {value, label} format 
                // sort/order here or on backend?  probably on backend... return sorted alphabetically (backend)
                // the objects here are from our CompItemSubFormSettingsRead thing
                const remappedItems = rawItems.map(ci => ({ value: ci.id, label: ci.title }));
                const emptyOption = { value: '', label: '-- Select Item --' };
                this.compItemOptionsById = Object.fromEntries(rawItems.map(ci => [ci.id, ci]));
                this.itemOptions = [emptyOption, ...remappedItems];
                const compItemTypesById = {};
                const compItemsByCompItemTypeId = {};  // keys are CIT ID, values are empty arrays
                rawItems.forEach(ci => {
                    const citId = ci.item_type.id;
                    compItemTypesById[citId] = ci.item_type;
                    compItemsByCompItemTypeId[citId] = compItemsByCompItemTypeId[citId] || [];
                    compItemsByCompItemTypeId[citId].push(ci);
                });
                const itemOptionGroups = {
                    'Please select...': [emptyOption],
                };
                Object.values(compItemTypesById).forEach(cit => {
                    // loop through unique CompItemTypes and build the groups
                    const compItemsForCIT = sortBy(compItemsByCompItemTypeId[cit.id], ['title']);
                    const citLabel = cit.name;
                    itemOptionGroups[citLabel] = compItemsForCIT.map(ci => ({ value: ci.id, label: ci.title }));
                });
                this.itemOptionGroups = itemOptionGroups;
            } catch (e) {
                this.apiErrorHandler(e);
            }
            this.loadingAvailableItems = false;
        },
        handleCompItemSelection(selectedCompItemId) {
            console.log('From handleCompItemSelection, selectedCompItemId:', selectedCompItemId);
            this.selectedCompItem = this.compItemOptionsById[selectedCompItemId] || null;
        },
        formatDataForSave(formData) {
            // do anything with the data before sending to API
            // ALSO, we do final validation here, return { data, errors } -- errors should be empty if all is good
            // must get the file IDs out and send those in array of 'files'
            console.log('data we need to format for save:', formData);
            const errors = [];  // just a list of strings, we will set on :form-errors (the form-level error prop on the form)
            // handle valid_from, valid_to, reminders, etc.
            /// WHY?  because we have to use 'keep-model-data' on formulate inputs to keep the form from breaking when we toggle things on/off
            //        so we have to 'clean' things here that should not be sent to the API
            let { validFromMode, validToMode, valid_from, valid_to, enableExpirationChoice, enableExpireActionsChoice, duration_num_periods, duration_period, reminder_action, ...saveData } = cloneDeep(formData);
            let enableExpiration = this.mustShowExpirationFields || enableExpirationChoice === 'yes';  // override our previous boolean with choice from radio
            let enableExpireActions = this.enableVciEas && enableExpireActionsChoice === 'yes';  // override our previous boolean with choice from radio
            // then set again based on those new booleans
            enableExpireActions = enableExpireActions && enableExpiration;  // if we're not tracking expiration, we can't set reminders
            // update saveData
            if (!enableExpiration) {
                // no expiration?  then blank our dates and set forever flags
                saveData['valid_from'] = null;
                saveData['valid_to'] = null;
                saveData['valid_from_forever'] = true;
                saveData['valid_to_forever'] = true;
            } else {
                // VALID FROM
                if (this.validFromRequired) {
                    saveData['valid_from'] = valid_from;
                    saveData['valid_from_forever'] = false;
                } else if (validFromMode === 'forever' || this.validFromDisabled) {
                    saveData['valid_from'] = null;
                    saveData['valid_from_forever'] = true;
                } else {
                    saveData['valid_from'] = valid_from;
                    saveData['valid_from_forever'] = false;
                }
                // VALID TO
                if (this.validToRequired) {
                    saveData['valid_to'] = valid_to;
                    saveData['valid_to_forever'] = false;
                } else if (validToMode === 'forever' || this.validToDisabled) {
                    saveData['valid_to'] = null;
                    saveData['valid_to_forever'] = true;
                } else {
                    saveData['valid_to'] = valid_to;
                    saveData['valid_to_forever'] = false;
                }
            }
            // check valid_from/valid_to relative to each other 
            // why check here?  because we don't have a reliable way to look at form data until it submits (because formulate is crappy)
            if (saveData['valid_from'] && saveData['valid_to']) {
                const fromDT = DateTime.fromISO(saveData['valid_from']);
                const toDT = DateTime.fromISO(saveData['valid_to']);
                if (fromDT > toDT) {
                    errors.push('Valid From date must be before Valid To date');
                }
            }
            // handle reminders separately
            if (enableExpireActions) {
                saveData['expire_action_data'] = {
                    duration_num_periods: duration_num_periods,
                    duration_period: duration_period,
                    reminder_action: reminder_action,
                }  // TODO - add more validation here
            } else {
                saveData['expire_action_data'] = null;
            }
            // once again, we need to transform the structure of 'files' -- from formulate, we'll have array of objects, hopefully with our ID in there
            // we need to pull out the ID and just send back a flat list of IDs to our backend
            let { files, ...otherData } = saveData;
            const dataForApi = cloneDeep(otherData);  // copy this first, then we'll add files back in after 
            if (files && Array.isArray(files) && files.length) {
                let fileIdsForApi = files.map(f => f.id);
                dataForApi['file_ids'] = fileIdsForApi;  // as long as 'file_ids' is still the key you want it as when posting to API
            }
            // make sure we have vendor_id in there also
            dataForApi['vendor_id'] = this.vendorId;
            return { data: dataForApi, errors: errors };
        },
        setSavingStatus(isSaving) {
            this.saving = isSaving;
            this.$emit('loading-status', isSaving);
        },
        handleFormInput(formData) {  // called on the FormulateForm @input event, which is kinda unreliable for nested data... but should be OK here for now
            // this is called on every input change, so you can do things like update other fields, etc.
            // TURNED OFF FOR NOW SINCE WE MOVED MOST THINGS TO THE TEMPLATE LOGIC
            console.log('*** From handleFormInput, here is formData we got:', formData);
        },
        async handleSubmit(formData) {  // called on the FormulateForm @submit event
            // this should be async, return a Promise, so Formulate shows isLoading while loading
            console.log('*** From handleSubmit, here is formData we got:');
            console.log(formData);
            let { data: dataToSubmit, errors: formErrors } = this.formatDataForSave(formData);
            console.log('*** Here is data we are going to submit to API:', dataToSubmit);
            if (formErrors.length) {
                this.formErrors = formErrors;
                return;  // don't submit if we have errors - should we do something else here to tell form it didn't succeed?
            }
            this.setSavingStatus(true);
            let returnData;
            try {
                if (this.isUpdate) {
                    // TODO - this needs work if you actually use it for updates -- but you should probably use flow object stuff for updates
                    // NOTE - we don't allow updates to versions right now... but maybe later...
                    const subId = this.existingData.id;
                    console.log('subId', subId)
                    let resp = {};
                    returnData = resp.data;
                    console.log('got response from updating submission', returnData);
                } else {  // if NOT an update, create new version
                    const resp = await suppliosApi.compSubStd.create_one_off_vendor_comp_item(dataToSubmit);
                    returnData = resp.data;
                    console.log('got response from creating new one-off item + submission', returnData);
                }
            } catch (err) {
                this.apiErrorHandler(err);
            }
            if (returnData) {
                if (this.isUpdate) {
                    this.$emit('updated', returnData);
                } else {
                    this.$emit('created', returnData);
                }
                this.$emit('saved', returnData);  // common for both update/create
            }
            this.setSavingStatus(false);
            return returnData;  // make sure not to return until after you set saving status back to false
        },
        async formulateUploader(file, progress, error, options) {
            // pass this function to 'uploader' prop of formulate-input 
            // must return a Promise that resolves to array of objects, at least with 'url' key in each object (but you also expect 'id' for your own backend handling)
            // and must accept 4 args (file, progress, error, option)
            this.uploadPending = true;  // set our flag to true so we can block things while uploading
            let file_category = this.fileCategory;
            let parentId = null;  // need this is we allow uploading the file before the submission is created!
            let title = '';  // you could pass title to asyncSplFileSlinger if you know it in advance
            let uploadResponse = await asyncSplFileSlinger(file_category, file, progress, error, options, title, parentId);
            this.uploadPending = false;
            console.log('Dumping resp from our file uploader:');
            console.log(uploadResponse);
            return uploadResponse;  // must return a promise here so formulate knows upload is done 
        },
    }
}
</script>

<style>

</style>